// src/views/contasContabeis/ContasContabeisList.jsx
import React, { useState, useEffect, useCallback, useMemo } from "react";
import contasContabeisService from "../../services/contasContabeisService";
import condominiosService from "../../services/condominiosService";
import { useAuth } from "../../contexts/AuthContext";
import ContaContabilModal from "./ContaContabilModal";
import CopiarContasModal from "./CopiarContasModal";
import DeleteModal from "../../components/ui/DeleteModal";
import Banner from "../../components/ui/Banner";
import ConfirmModal from "../../components/ui/ConfirmModal";

// Quantos pontos a classificação tem → define o nível de indentação
function nivelClassificacao(cls) {
  if (!cls) return 0;
  return String(cls).split(".").length - 1;
}

// Exporta linhas para CSV (UTF-8 com BOM para Excel abrir corretamente)
function exportarCSV(rows) {
  const headers = ["Classificação","Descrição","Natureza","Tipo","Categoria","Grupo Contábil","Db/Cr","Saldo Inicial","Saldo Atual","Situação"];
  const csv = [
    headers.join(";"),
    ...rows.map(r => [
      r.classificacaoContabil || "",
      `"${(r.descConta||"").replace(/"/g,'""')}"`,
      r.natureza || "",
      r.tpConta || "",
      TP_CONTA_CATEGORIA[r.tpConta] || "Outro",
      `"${(r.grpContabil||"").replace(/"/g,'""')}"`,
      r.tpGrupo === "Credito" ? "C" : "D",
      Number(r.saldoInicial||0).toFixed(2).replace(".",","),
      Number(r.saldoConta||0).toFixed(2).replace(".",","),
      r.situacao || "",
    ].join(";")),
  ].join("\r\n");
  const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement("a");
  a.href = url;
  a.download = `plano-contas-${new Date().toISOString().split("T")[0]}.csv`;
  document.body.appendChild(a); a.click(); document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

const TP_CONTA_LABELS = {
  ReceitaOrdinaria:"Receita Ordinária", ReceitaExtraordinaria:"Receita Extraordinária",
  DespesaAdministrativa:"Desp. Administrativa", DespesaOperacional:"Desp. Operacional",
  DespesaFinanceira:"Desp. Financeira", FundoReserva:"Fundo de Reserva",
  AtivoCirculante:"Ativo Circulante", AtivoNaoCirculante:"Ativo Não Circulante",
  PassivoCirculante:"Passivo Circulante", PassivoNaoCirculante:"Passivo Não Circulante",
  PatrimonioLiquido:"Patrimônio Líquido", Caixa:"Caixa", Banco:"Banco",
  ContasPagar:"Contas a Pagar", ContasReceber:"Contas a Receber", Outro:"Outro",
};

const TP_CONTA_COLORS = {
  ReceitaOrdinaria:"#DCFCE7:#166534", ReceitaExtraordinaria:"#DCFCE7:#166534",
  DespesaAdministrativa:"#FEE2E2:#991B1B", DespesaOperacional:"#FEE2E2:#991B1B",
  DespesaFinanceira:"#FEE2E2:#991B1B", FundoReserva:"#F5F3FF:#6D28D9",
  AtivoCirculante:"#EFF6FF:#1D4ED8", AtivoNaoCirculante:"#EFF6FF:#1D4ED8",
  PassivoCirculante:"#FFF7ED:#C2410C", PassivoNaoCirculante:"#FFF7ED:#C2410C",
  PatrimonioLiquido:"#F5F3FF:#6D28D9", Caixa:"#ECFDF5:#065F46",
  Banco:"#ECFDF5:#065F46", ContasPagar:"#FFF7ED:#C2410C",
  ContasReceber:"#DCFCE7:#166534", Outro:"var(--gray-100):var(--gray-500)",
};

// Mapa tpConta → Categoria semântica (Receita/Despesa/Ativo/Passivo/Patrimonial/Outro)
const TP_CONTA_CATEGORIA = {
  ReceitaOrdinaria:"Receita", ReceitaExtraordinaria:"Receita",
  DespesaAdministrativa:"Despesa", DespesaOperacional:"Despesa", DespesaFinanceira:"Despesa",
  FundoReserva:"Patrimonial", PatrimonioLiquido:"Patrimonial",
  AtivoCirculante:"Ativo", AtivoNaoCirculante:"Ativo", Caixa:"Ativo", Banco:"Ativo", ContasReceber:"Ativo",
  PassivoCirculante:"Passivo", PassivoNaoCirculante:"Passivo", ContasPagar:"Passivo",
  Outro:"Outro",
};

const CATEGORIA_STYLE = {
  Receita:     { bg:"#DCFCE7", color:"#166534", icon:"bi-graph-up-arrow"   },
  Despesa:     { bg:"#FEE2E2", color:"#991B1B", icon:"bi-graph-down-arrow" },
  Ativo:       { bg:"#DBEAFE", color:"#1E40AF", icon:"bi-wallet2"          },
  Passivo:     { bg:"#FFEDD5", color:"#9A3412", icon:"bi-credit-card"      },
  Patrimonial: { bg:"#EDE9FE", color:"#5B21B6", icon:"bi-piggy-bank"       },
  Outro:       { bg:"#F1F5F9", color:"var(--gray-600)", icon:"bi-three-dots"       },
};

function getColor(tp) {
  const c = TP_CONTA_COLORS[tp]||"var(--gray-100):var(--gray-500)";
  const [bg,color] = c.split(":");
  return { background:bg, color };
}

function getCategoria(tpConta) {
  return TP_CONTA_CATEGORIA[tpConta] || "Outro";
}

export default function ContasContabeisList() {
  const { condominioAtivo, isAdmin, isGerente, podeEditar } = useAuth();
  const canEdit = podeEditar("contasContabeis");
  const [data, setData]             = useState([]);
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState(null);
  const [condominios, setCondominios] = useState([]);

  // Sync with navbar condominioAtivo — updates whenever user changes it
  useEffect(() => {
    if (condominioAtivo?.id) setIdCondo(String(condominioAtivo.id));
    else setIdCondo("");
  }, [condominioAtivo]);
  const [idCondo, setIdCondo]       = useState("");
  const [search, setSearch]         = useState("");
  const [filterTpConta, setFilterTpConta] = useState("");
  const [filterSituacao, setFilterSituacao] = useState("");
  const [filterNatureza, setFilterNatureza] = useState("");
  const [filterGrupo, setFilterGrupo] = useState("");

  const [filterCategoria, setFilterCategoria] = useState("");

  const [showModal, setShowModal]     = useState(false);
  const [showCopiar, setShowCopiar]   = useState(false);
  const [editData, setEditData]       = useState(null);
  const [showDelete, setShowDelete]   = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [saving, setSaving]           = useState(false);
  const [deleting, setDeleting]       = useState(false);
  const [importando, setImportando]   = useState(false);
  const [viewMode, setViewMode]       = useState("hierarquia"); // "hierarquia" | "lista"

  // UX: banner global + modal de confirmação genérico
  const [banner, setBanner] = useState(null);
  const [confirmacao, setConfirmacao] = useState(null);
  const notify = useCallback((tipo, msg) => {
    setBanner({ tipo, msg });
    if (tipo === "success" || tipo === "info") {
      setTimeout(() => setBanner((b) => (b && b.msg === msg ? null : b)), 4000);
    }
  }, []);
  const askConfirm = useCallback(
    (cfg) => new Promise((resolve) => {
      setConfirmacao({
        titulo: cfg.titulo || "Confirmar",
        mensagem: cfg.mensagem,
        perigo: !!cfg.perigo,
        textoOk: cfg.textoOk || "Confirmar",
        onConfirm: () => { setConfirmacao(null); resolve(true); },
        onCancel:  () => { setConfirmacao(null); resolve(false); },
      });
    }),
    []
  );

  useEffect(() => {
    condominiosService.listar({ limit:100 }).then(r => {
      setCondominios(r.data);

    }).catch(()=>{});
  }, []);

  const carregar = useCallback(async () => {
    if (!idCondo) return;
    setLoading(true); setError(null);
    try {
      const res = await contasContabeisService.listar({
        idCondominio:idCondo, search, tpConta:filterTpConta,
        situacao:filterSituacao, natureza:filterNatureza,
      });
      let rows = res.data;
      if (filterGrupo)     rows = rows.filter(r=>r.tpGrupo===filterGrupo);
      if (filterCategoria) rows = rows.filter(r=>getCategoria(r.tpConta)===filterCategoria);
      setData(rows);
    } catch(err) { setError(err.message); }
    finally { setLoading(false); }
  }, [idCondo, search, filterTpConta, filterSituacao, filterNatureza, filterGrupo, filterCategoria]);

  useEffect(() => { carregar(); }, [carregar]);

  const condoNome = condominios.find(c=>String(c.id)===String(idCondo))?.nmFantasia || "";
  const stats = {
    total:      data.length,
    receitas:   data.filter(c=>getCategoria(c.tpConta)==="Receita").length,
    despesas:   data.filter(c=>getCategoria(c.tpConta)==="Despesa").length,
    bancoCaixa: data.filter(c=>["Banco","Caixa"].includes(c.tpConta)).length,
    outros:     data.filter(c=>!["Receita","Despesa"].includes(getCategoria(c.tpConta)) && !["Banco","Caixa"].includes(c.tpConta)).length,
  };

  const handleSave = async (form) => {
    setSaving(true);
    try {
      const payload = { ...form, idCondominio: idCondo };
      if (form.id) await contasContabeisService.atualizar(form.id, payload);
      else         await contasContabeisService.criar(payload);
      setShowModal(false); await carregar();
      notify("success", form.id ? "Conta atualizada." : "Conta criada.");
    } catch(err) { notify("error", err.message); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await contasContabeisService.deletar(deleteTarget.id);
      setShowDelete(false); setDeleteTarget(null); await carregar();
      notify("success", "Conta excluída.");
    } catch(err) {
      const msg = err?.message || "Erro ao excluir conta.";
      const emUso = (msg.toLowerCase().includes("uso") || msg.toLowerCase().includes("inative")) && deleteTarget?.id;
      if (emUso) {
        setShowDelete(false);
        const ok = await askConfirm({
          titulo: "Conta em uso",
          mensagem: `${msg}\n\nDeseja inativar a conta agora em vez de excluir?`,
          textoOk: "Inativar conta",
        });
        if (ok) {
          try {
            await contasContabeisService.atualizar(deleteTarget.id, { situacao: "inativo" });
            setDeleteTarget(null); await carregar();
            notify("success", "Conta inativada.");
          } catch(e2) { notify("error", e2?.message || "Erro ao inativar."); }
        } else {
          setDeleteTarget(null);
        }
      } else {
        notify("error", msg);
      }
    }
    finally { setDeleting(false); }
  };

  const handleImportarPadrao = async () => {
    if (!idCondo) return;
    const ok = await askConfirm({
      titulo: "Importar plano padrão",
      mensagem: "Importar plano de contas padrão para este condomínio? Serão criadas aproximadamente 38 contas.",
      textoOk: "Importar",
    });
    if (!ok) return;
    setImportando(true);
    try {
      const r = await contasContabeisService.importarPlanoPadrao(idCondo);
      notify("success", r.message || `${r.imported || 0} contas importadas.`);
      await carregar();
    } catch(err) { notify("error", err.message); }
    finally { setImportando(false); }
  };

  const [normalizando, setNormalizando] = useState(false);
  const handleNormalizar = async () => {
    if (!idCondo) return;
    const ok = await askConfirm({
      titulo: "Normalizar plano de contas",
      mensagem:
        "Esta ação reaplica as regras contábeis canônicas:\n\n" +
        "• Receitas ⇒ Natureza = Crédito\n" +
        "• Despesas ⇒ Natureza = Débito\n" +
        "• Ativos ⇒ Natureza = Débito | Passivos/PL ⇒ Crédito\n" +
        "• Grupo Contábil é corrigido se estiver vazio ou inconsistente\n\n" +
        "Não altera lançamentos. Continuar?",
      textoOk: "Normalizar",
    });
    if (!ok) return;
    setNormalizando(true);
    try {
      const r = await contasContabeisService.normalizar(idCondo);
      const detalhes = (r.detalhes || []).slice(0, 5).map(d =>
        `${d.classificacao} ${d.descricao}\n   tpGrupo: ${d.de.tpGrupo} → ${d.para.tpGrupo}\n   grupo: ${d.de.grpContabil||"(vazio)"} → ${d.para.grpContabil}`
      ).join("\n\n");
      const msg = r.contasAjustadas === 0
        ? "Nenhum ajuste necessário — plano já está consistente."
        : `${r.message}${detalhes ? "\n\nExemplos:\n" + detalhes : ""}`;
      notify(r.contasAjustadas === 0 ? "info" : "success", msg);
      await carregar();
    } catch(err) { notify("error", err.message); }
    finally { setNormalizando(false); }
  };

  const fmtMoeda = v => v!=null ? Number(v).toLocaleString("pt-BR",{style:"currency",currency:"BRL"}) : "—";

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title"><i className="bi bi-journal-bookmark me-2" style={{ color:"var(--primary)",fontSize:22 }}/>Plano de Contas</h1>
          <p className="page-subtitle">Gerencie o plano de contas contábil do condomínio</p>
        </div>
        <div className="d-flex gap-2 align-items-center flex-wrap">
          <button className="btn btn-outline-secondary" onClick={()=>exportarCSV(data)} disabled={!data.length} title="Exportar para CSV/Excel">
            <i className="bi bi-file-earmark-spreadsheet"/> Exportar CSV
          </button>
          {stats.total === 0 && idCondo && canEdit && (
            <button className="btn btn-outline-success" onClick={handleImportarPadrao} disabled={importando} title="Importar plano padrão de contas para condomínios">
              {importando
                ? <><span className="spinner-border spinner-border-sm me-1"/>Importando...</>
                : <><i className="bi bi-magic"/> Importar Plano Padrão</>}
            </button>
          )}
          {stats.total > 0 && idCondo && canEdit && (
            <button className="btn btn-outline-warning" onClick={handleNormalizar} disabled={normalizando}
              title="Reaplica regras canônicas: Receita⇒Crédito, Despesa⇒Débito, corrige grpContabil inconsistente">
              {normalizando
                ? <><span className="spinner-border spinner-border-sm me-1"/>Normalizando...</>
                : <><i className="bi bi-shield-check"/> Normalizar</>}
            </button>
          )}
          <button className="btn btn-outline-secondary" onClick={()=>setShowCopiar(true)} disabled={!idCondo} title="Copiar plano de contas de outro condomínio">
            <i className="bi bi-copy"/> Copiar de outro
          </button>
          <button className="btn btn-primary" onClick={()=>{ setEditData(null); setShowModal(true); }} disabled={!idCondo || !canEdit}>
            <i className="bi bi-plus-lg"/> Nova Conta
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="row g-3 mb-4">
        {[
          {icon:"bi-journal-bookmark", cls:"blue",   val:stats.total,      label:"Total de Contas"},
          {icon:"bi-graph-up-arrow",   cls:"green",  val:stats.receitas,   label:"Receitas"},
          {icon:"bi-graph-down-arrow", cls:"orange", val:stats.despesas,   label:"Despesas"},
          {icon:"bi-bank",             cls:"gray",   val:stats.bancoCaixa, label:"Banco / Caixa"},
          {icon:"bi-three-dots",       cls:"blue",   val:stats.outros,     label:"Outros"},
        ].map(s=>(
          <div className="col-6 col-md-2" key={s.label}>
            <div className="stat-card">
              <div className={`stat-icon ${s.cls}`}><i className={`bi ${s.icon}`}/></div>
              <div><div className="stat-value">{s.val}</div><div className="stat-label">{s.label}</div></div>
            </div>
          </div>
        ))}
      </div>

      <div className="card">
        {/* Toolbar */}
        <div className="card-header-custom" style={{ flexWrap:"wrap",gap:8 }}>
          <div className="search-wrapper" style={{ minWidth:200 }}>
            <i className="bi bi-search"/>
            <input className="search-input" placeholder="Buscar classificação, descrição..." value={search} onChange={e=>setSearch(e.target.value)}/>
          </div>
          <div className="d-flex gap-2 flex-wrap">
            <select className="form-select form-select-sm" style={{ width:"auto",fontSize:13 }} value={filterCategoria} onChange={e=>setFilterCategoria(e.target.value)}>
              <option value="">Todas as categorias</option>
              <option value="Receita">Receita</option>
              <option value="Despesa">Despesa</option>
              <option value="Ativo">Ativo</option>
              <option value="Passivo">Passivo</option>
              <option value="Patrimonial">Patrimonial</option>
              <option value="Outro">Outro</option>
            </select>
            <select className="form-select form-select-sm" style={{ width:"auto",fontSize:13 }} value={filterGrupo} onChange={e=>setFilterGrupo(e.target.value)}>
              <option value="">Db/Cr</option>
              <option value="Debito">Débito</option>
              <option value="Credito">Crédito</option>
            </select>
            <select className="form-select form-select-sm" style={{ width:"auto",fontSize:13 }} value={filterNatureza} onChange={e=>setFilterNatureza(e.target.value)}>
              <option value="">Todas as naturezas</option>
              <option value="Analitica">Analítica</option>
              <option value="Sintetica">Sintética</option>
            </select>
            <select className="form-select form-select-sm" style={{ width:"auto",fontSize:13 }} value={filterTpConta} onChange={e=>setFilterTpConta(e.target.value)}>
              <option value="">Todos os tipos</option>
              {Object.entries(TP_CONTA_LABELS).map(([v,l])=><option key={v} value={v}>{l}</option>)}
            </select>
            <select className="form-select form-select-sm" style={{ width:"auto",fontSize:13 }} value={filterSituacao} onChange={e=>setFilterSituacao(e.target.value)}>
              <option value="">Todas</option>
              <option value="ativo">Ativas</option>
              <option value="inativo">Inativas</option>
            </select>
            <div className="btn-group btn-group-sm" role="group" title="Modo de visualização">
              <button type="button" className={`btn ${viewMode==="hierarquia"?"btn-primary":"btn-outline-secondary"}`} onClick={()=>setViewMode("hierarquia")} title="Mostrar hierarquia com indentação">
                <i className="bi bi-diagram-3"/>
              </button>
              <button type="button" className={`btn ${viewMode==="lista"?"btn-primary":"btn-outline-secondary"}`} onClick={()=>setViewMode("lista")} title="Lista plana">
                <i className="bi bi-list-ul"/>
              </button>
            </div>
            <button className="btn btn-outline-secondary btn-sm" onClick={carregar}><i className="bi bi-arrow-clockwise"/></button>
          </div>
        </div>

        {error && <div className="alert alert-danger m-3 mb-0" style={{ fontSize:13 }}><i className="bi bi-exclamation-triangle-fill me-2"/>{error}</div>}

        <div className="table-responsive">
          <table className="table table-custom">
            <thead>
              <tr>
                <th>Classificação</th>
                <th>Descrição</th>
                <th>Natureza</th>
                <th>Categoria</th>
                <th>Tipo de Conta</th>
                <th style={{ minWidth:160 }}>Grupo Contábil</th>
                <th>Db/Cr</th>
                <th style={{ textAlign:"right" }}>Saldo</th>
                <th>Situação</th>
                <th style={{ width:100 }}>Ações</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={10} className="text-center py-5"><span className="spinner-border spinner-border-sm text-primary me-2"/><span style={{ color:"var(--gray-500)" }}>Carregando...</span></td></tr>
              ) : !idCondo ? (
                <tr><td colSpan={10} className="text-center py-5" style={{ color:"var(--gray-400)" }}><i className="bi bi-buildings" style={{ fontSize:32,display:"block",marginBottom:8 }}/>Selecione um condomínio</td></tr>
              ) : data.length===0 ? (
                <tr><td colSpan={10} className="text-center py-5" style={{ color:"var(--gray-400)" }}>
                  <i className="bi bi-journal-bookmark" style={{ fontSize:32,display:"block",marginBottom:8 }}/>
                  <span style={{ fontSize:14 }}>Nenhuma conta encontrada</span><br/>
                  <small>Cadastre contas, importe o plano padrão ou copie de outro condomínio</small>
                  {canEdit && (
                    <div className="d-flex gap-2 justify-content-center mt-3 flex-wrap">
                      <button className="btn btn-outline-success btn-sm" onClick={handleImportarPadrao} disabled={importando}>
                        {importando ? <span className="spinner-border spinner-border-sm me-1"/> : <i className="bi bi-magic me-1"/>}
                        Importar Plano Padrão
                      </button>
                      <button className="btn btn-outline-secondary btn-sm" onClick={()=>setShowCopiar(true)}>
                        <i className="bi bi-copy"/> Copiar de outro condomínio
                      </button>
                      <button className="btn btn-primary btn-sm" onClick={()=>{ setEditData(null); setShowModal(true); }}>
                        <i className="bi bi-plus-lg"/> Nova Conta
                      </button>
                    </div>
                  )}
                </td></tr>
              ) : data.map(c=>{
                const nivel = viewMode === "hierarquia" ? nivelClassificacao(c.classificacaoContabil) : 0;
                const indentPx = nivel * 16;
                return (
                <tr key={c.id} style={{ opacity:c.situacao==="inativo"?.6:1, background:c.natureza==="Sintetica"?"#F8FAFC":"transparent" }}>
                  <td>
                    <span style={{ paddingLeft:indentPx, display:"inline-flex", alignItems:"center", gap:6 }}>
                      {nivel > 0 && viewMode === "hierarquia" && (
                        <span style={{ color:"var(--gray-300)", fontSize:10 }}>└─</span>
                      )}
                      <i className={`bi ${c.natureza==="Sintetica" ? "bi-folder-fill" : "bi-file-text"}`}
                         style={{ color: c.natureza==="Sintetica" ? "#7C3AED" : "var(--gray-400)", fontSize: 12 }}/>
                      <span style={{ fontFamily:"monospace",fontWeight:c.natureza==="Sintetica"?700:600,color:"var(--primary)",fontSize:13 }}>
                        {c.classificacaoContabil}
                      </span>
                    </span>
                  </td>
                  <td>
                    <div style={{ fontWeight: c.natureza==="Sintetica"?700:400,color:"var(--gray-800)",fontSize:13, paddingLeft:indentPx }}>
                      {c.descConta}
                    </div>
                  </td>
                  <td>
                    <span style={{ background:c.natureza==="Sintetica"?"#F5F3FF":"#EFF6FF",color:c.natureza==="Sintetica"?"#6D28D9":"var(--primary)",padding:"2px 8px",borderRadius:12,fontSize:11,fontWeight:600 }}>
                      {c.natureza==="Sintetica"?"Sintética":"Analítica"}
                    </span>
                  </td>
                  <td>
                    {(() => {
                      const cat = getCategoria(c.tpConta);
                      const st  = CATEGORIA_STYLE[cat] || CATEGORIA_STYLE.Outro;
                      return (
                        <span style={{ background:st.bg, color:st.color, padding:"2px 8px", borderRadius:12, fontSize:11, fontWeight:700, display:"inline-flex", alignItems:"center", gap:4 }}>
                          <i className={`bi ${st.icon}`} style={{ fontSize:10 }}/>{cat}
                        </span>
                      );
                    })()}
                  </td>
                  <td>
                    <span style={{ ...getColor(c.tpConta),padding:"2px 8px",borderRadius:12,fontSize:11,fontWeight:600 }}>
                      {TP_CONTA_LABELS[c.tpConta]||c.tpConta}
                    </span>
                  </td>
                  <td style={{ fontSize:12.5, color:"var(--gray-700)", fontWeight:500 }}>
                    {c.grpContabil ? c.grpContabil : <span style={{ color:"var(--gray-400)" }}>—</span>}
                  </td>
                  <td>
                    <span style={{ background:c.tpGrupo==="Credito"?"#DCFCE7":"#FEE2E2",color:c.tpGrupo==="Credito"?"#166534":"#991B1B",padding:"2px 8px",borderRadius:12,fontSize:11,fontWeight:600 }}>
                      {c.tpGrupo==="Credito"?"Crédito":"Débito"}
                    </span>
                  </td>
                  <td style={{ textAlign:"right",fontFamily:"monospace",fontSize:12.5,fontWeight:600,color:Number(c.saldoConta)>=0?"#166534":"#991B1B" }}>
                    {fmtMoeda(c.saldoConta)}
                  </td>
                  <td>
                    <span className={`badge-status ${c.situacao==="ativo"?"badge-ativo":"badge-inativo"}`}>
                      <i className="bi bi-circle-fill" style={{ fontSize:7 }}/>{c.situacao==="ativo"?"Ativa":"Inativa"}
                    </span>
                  </td>
                  <td>
                    <div className="table-actions">
                      <button className="btn btn-outline-secondary btn-sm btn-icon" onClick={()=>{ setEditData(c); setShowModal(true); }} disabled={!canEdit}><i className="bi bi-pencil"/></button>
                      <button className="btn btn-outline-danger btn-sm btn-icon" onClick={()=>{ setDeleteTarget(c); setShowDelete(true); }} disabled={!canEdit}><i className="bi bi-trash3"/></button>
                    </div>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {data.length>0 && (
          <div style={{ padding:"10px 20px",borderTop:"1px solid var(--gray-100)",fontSize:12.5,color:"var(--gray-500)",display:"flex",justifyContent:"space-between",flexWrap:"wrap",gap:8 }}>
            <span><strong>{data.length}</strong> conta(s) exibida(s)</span>
            <span>
              Analíticas: <strong>{data.filter(c=>c.natureza==="Analitica").length}</strong> |
              Sintéticas: <strong>{data.filter(c=>c.natureza==="Sintetica").length}</strong> |
              Ativas: <strong>{data.filter(c=>c.situacao==="ativo").length}</strong>
            </span>
          </div>
        )}
      </div>

      <ContaContabilModal show={showModal} onClose={()=>setShowModal(false)} onSave={handleSave}
        editData={editData} saving={saving} condominioNome={condoNome}/>
      <CopiarContasModal show={showCopiar} onClose={()=>setShowCopiar(false)} onCopiado={carregar}
        condominios={condominios} idCondominioAtual={idCondo} notify={notify} askConfirm={askConfirm}/>
      <DeleteModal show={showDelete} onClose={()=>setShowDelete(false)} onConfirm={handleDelete}
        itemName={deleteTarget?.descConta} loading={deleting}/>

      {banner && (
        <div style={{ position:"fixed", top:16, right:16, zIndex:9999, maxWidth:480 }}>
          <Banner tipo={banner.tipo} onClose={()=>setBanner(null)}>{banner.msg}</Banner>
        </div>
      )}
      {confirmacao && (
        <ConfirmModal
          titulo={confirmacao.titulo}
          mensagem={confirmacao.mensagem}
          perigo={confirmacao.perigo}
          textoOk={confirmacao.textoOk}
          onConfirm={confirmacao.onConfirm}
          onCancel={confirmacao.onCancel}
        />
      )}
    </div>
  );
}
